import { useRef, useEffect, useLayoutEffect, useState } from 'react'
import { useVirtualizer, type Virtualizer as TanStackVirtualizer } from '@tanstack/react-virtual'

/**
 * Virtualizer wraps @tanstack/react-virtual with two modes:
 * - Default: owns the scroll container (div with overflow-y-auto). Use when the list is the only scrollable content.
 * - External scroll (scrollElementRef + paddingStart): uses a parent element as the scroll container so the list
 *   can sit below other content (e.g. header + summary) and the whole page scrolls. paddingStart is the height
 *   of that content above the list so the virtualizer computes the correct visible range.
 */

type ScrollToFn = (
  offset: number,
  options: { adjustments?: number; behavior?: 'auto' | 'smooth' },
  instance: TanStackVirtualizer<HTMLDivElement, HTMLDivElement>,
) => void

type VirtualizerProps<T> = {
  items: T[]
  children: (item: T, index: number) => React.ReactNode
  /** Row height in px, or function (index) => px; used for scroll math. Include gap only if you do not use the gap prop. */
  estimateSize?: number | ((index: number) => number)
  /** Extra rows rendered above/below viewport to avoid blank flash while scrolling. */
  overscan?: number
  /** Spacing in px between rows; virtualizer includes this in layout. */
  gap?: number
  /** When true, each row is measured after render and scroll height updates (variable-height rows). */
  enableDynamicMeasurement?: boolean
  className?: string
  itemClassName?: string
  /** Stable key per row; used for React reconciliation. Omit to use virtualizer index. */
  getItemKey?: (item: T, index: number) => string | number
  /** Custom scroll implementation; see TanStack Virtual scrollToFn. */
  scrollToFn?: ScrollToFn
  /** When this value's identity changes, the list scrolls to top (index 0). */
  scrollToTopDependency?: unknown
  /** When set, use this element as the scroll container instead of an internal div (for whole-page scroll). */
  scrollElementRef?: React.RefObject<HTMLDivElement | null>
  /** Offset in px from top of scroll container where the list content starts (use with scrollElementRef). */
  paddingStart?: number
  /**
   * When true, expose list/listitem roles for screen readers (e.g. withdrawals).
   * Also sets aria-setsize / aria-posinset so TalkBack announces the full count
   * even though only the virtualized window is in the DOM (View Withdrawals_B07).
   */
  listSemantics?: boolean
}

export function Virtualizer<T>({
  items,
  children,
  estimateSize = 100,
  overscan = 5,
  gap = 0,
  enableDynamicMeasurement = false,
  className = '',
  itemClassName = '',
  getItemKey,
  scrollToFn,
  scrollToTopDependency,
  scrollElementRef,
  paddingStart = 0,
  listSemantics = false,
}: VirtualizerProps<T>) {
  const parentRef = useRef<HTMLDivElement>(null)
  const [scrollRect, setScrollRect] = useState<{ width: number; height: number } | null>(null)

  // With an external scroll element, TanStack Virtual observes it for viewport size; until that runs it falls back
  // to initialRect (default 0×0), so getVirtualItems() returns [] and nothing renders. We measure the scroll
  // element and pass initialRect so the virtualizer has a valid viewport from the first paint.
  useLayoutEffect(() => {
    if (!scrollElementRef?.current) return
    const el = scrollElementRef.current
    const update = () => {
      const { width, height } = el.getBoundingClientRect()
      setScrollRect((prev) =>
        prev?.width === width && prev?.height === height ? prev : { width, height }
      )
    }
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [scrollElementRef])

  const useExternalScroll = scrollElementRef != null

  /**
   * For external scroll: list starts at paddingStart in the scroll container; 
   * initialRect gives viewport size until TanStack's observer runs.
   */
  const virtualizerOptions: Record<string, unknown> = {
    count: items.length,
    getScrollElement: () => (scrollElementRef ? scrollElementRef.current : parentRef.current),
    estimateSize:
      typeof estimateSize === 'function'
        ? estimateSize
        : () => estimateSize,
    overscan,
  }
  if (gap > 0) virtualizerOptions.gap = gap
  if (scrollToFn) virtualizerOptions.scrollToFn = scrollToFn
  if (useExternalScroll) {
    virtualizerOptions.paddingStart = paddingStart
    if (scrollRect != null && scrollRect.height > 0) {
      virtualizerOptions.initialRect = scrollRect
    }
  }

  const virtualizer = useVirtualizer(virtualizerOptions as Parameters<typeof useVirtualizer>[0])

  // When the consumer signals "reset" (e.g. filter changed), scroll the list back to the first item.
  useEffect(() => {
    if (items.length > 0) {
      virtualizer.scrollToIndex(0, { behavior: 'auto' })
    }
  }, [scrollToTopDependency])

  const virtualItems = virtualizer.getVirtualItems()
  const totalSize = virtualizer.getTotalSize()
  // With external scroll the list is a sibling of the content above it; we only render the list region. TanStack's
  // getTotalSize() includes paddingStart, so we subtract it to get the height of the list block we actually render.
  const innerHeight =
    scrollElementRef != null ? totalSize - paddingStart : totalSize
  // virtualRow.start is in "full scroll content" coordinates (includes paddingStart). Our inner div is list-only,
  // so we subtract paddingStart to get the position inside that div.
  const itemOffset = scrollElementRef != null ? -paddingStart : 0

  const inner = (
    <div
      role={listSemantics ? 'list' : undefined}
      className="relative w-full"
      style={{ height: `${innerHeight}px` }}
    >
      {virtualItems.map((virtualRow) => {
        const item = items[virtualRow.index]
        const key = getItemKey ? getItemKey(item, virtualRow.index) : virtualRow.key
        const style: React.CSSProperties = {
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          transform: `translateY(${itemOffset + virtualRow.start}px)`,
        }
        if (enableDynamicMeasurement) {
          style.minHeight = `${virtualRow.size}px`
        } else {
          style.height = `${virtualRow.size}px`
        }
        return (
          <div
            key={key}
            role={listSemantics ? 'listitem' : undefined}
            aria-setsize={listSemantics ? items.length : undefined}
            aria-posinset={listSemantics ? virtualRow.index + 1 : undefined}
            ref={enableDynamicMeasurement ? virtualizer.measureElement : undefined}
            data-index={enableDynamicMeasurement ? virtualRow.index : undefined}
            className={itemClassName}
            style={style}
          >
            {children(item, virtualRow.index)}
          </div>
        )
      })}
    </div>
  )

  // External scroll: parent already provides the scroll container; we only output the list content (no overflow div).
  if (scrollElementRef) {
    return <div className={className}>{inner}</div>
  }

  // Default: we own the scroll container so the list has something to scroll within.
  return (
    <div ref={parentRef} className={`h-full overflow-y-auto overflow-x-hidden ${className}`}>
      {inner}
    </div>
  )
}
